import React, { useCallback, useMemo, useState } from "react";

// region Libraries
import { Autocomplete, CircularProgress, TextField } from "@libraries/mui";
// endregion Libraries
// region Store
import utils from "@helpers/utils";
// endregion Store
// region Graphql
import { ClientType } from "@store/graphql/helpers/types.graphql";
// endregion Graphql
// region Languages
import { GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Styles
import * as Styled from "./styles";

// endregion Styles

interface Props {
  selectedClient: ClientType;
  setSelectedClient: React.Dispatch<React.SetStateAction<ClientType>>;
  handleLastElement: () => void;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  options: ClientType[];
}

const SelectClient: React.FC<Props> = ({
  selectedClient,
  setSelectedClient,
  handleLastElement,
  setFilter,
  loading,
  options
}) => {
  const [inputText, setInputText] = useState<string>("");
  const { t } = useTranslation();

  /**
   * Handle change on select client
   */
  const handleChange = useCallback(
    (_event: any, value: any) => {
      setSelectedClient(value);
    },
    [setSelectedClient]
  );

  /**
   * Handle scroll on select client
   * @param event scroll reference event
   */
  const handleScroll = (event) => {
    if (!event?.target) return;

    const { target } = event;

    if (!loading && target.scrollTop + target.clientHeight === target.scrollHeight) {
      handleLastElement();
    }
  };

  /**
   * Handle input change on select client
   * @param event input reference event
   * @param newInputValue new input value
   */
  const handleInputChange = (event, newInputValue) => {
    setInputText(newInputValue);
    setFilter(newInputValue);
  };

  // Memoize dropdown to avoid re-render
  const dropdown = useMemo(() => (
    <Autocomplete
      disablePortal
      id="select-client"
      options={options}
      noOptionsText={inputText.length > 2 ? t(GlobalMessages.noItemsText) : t(GlobalMessages.clientSelectInstruction)}
      getOptionLabel={(option) => (option.externalId && option.document && option.alias
        ? `(${option.externalId}) ${option.document} - ${option.alias}`
        : "")}
      defaultValue={{ externalId: "asd", document: "", alias: "" } as ClientType}
      disableClearable
      value={selectedClient}
      onChange={handleChange}
      onInputChange={handleInputChange}
      ListboxProps={{
        onScroll: handleScroll,
        role: "list-box"
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t(GlobalMessages.selectClient)}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          id={option.id}
          key={option.id}
        >
          ({option.externalId}) {utils.formatCpfCnpj(option.document)} - {option.alias}
        </li>
      )}
    />
  ), [options, handleChange, selectedClient, t, loading]);

  return <Styled.Container>{dropdown}</Styled.Container>;
};

export default SelectClient;
